import React from "react"
import styled, {keyframes} from "styled-components"
import Layout from "../Layout";
import {BigCenter, Center, PageTitle} from "../shared/mix";
import CTA from "../CTA";
import {Link} from "react-router-dom";
import {Btn} from "../Welcome/WelcomeBanner";
import SEO from "../SEO";

const services = [
    {
        icon: "fa-solid fa-window-maximize",
        title: "Webové stránky",
        desc: "Moderní, rychlé a responzivní weby šité na míru. Od firemních prezentací po komplexní portály — vždy s důrazem na výkon a uživatelský zážitek.",
        tags: ["React", "Next.js", "PHP & Laravel"],
    },
    {
        icon: "fa-solid fa-mobile-screen-button",
        title: "PWA & mobilní aplikace",
        desc: "Progresivní webové aplikace, které fungují i offline, a mobilní aplikace v React Native. Jeden tým, jedna kódová základna — dvě platformy.",
        tags: ["React Native", "PWA", "iOS & Android"],
    },
    {
        icon: "fa-solid fa-plug",
        title: "Propojení systémů",
        desc: "Napojení na externí služby, API integrace a automatizace datových toků mezi systémy. Propojím váš web s CRM, fakturací, skladem nebo čímkoli dalším.",
        tags: ["REST API", "Webhooks", "Automatizace"],
    },
    {
        icon: "fa-solid fa-server",
        title: "Správa & IT služby",
        desc: "Hosting, vlastní doména, firemní e-maily, SSL certifikáty a průběžná údržba. Kompletní technické zázemí, ať se můžete soustředit na své podnikání.",
        tags: ["E-mail", "DNS & domény", "Hosting"],
    },
    {
        icon: "fa-solid fa-gears",
        title: "Webové aplikace na míru",
        desc: "Interní firemní systémy, rezervační platformy, dashboardy a nástroje na míru. Řešení, která šetří čas a zjednodušují procesy.",
        tags: ["Node.js", "Laravel", "Databáze"],
    },
    {
        icon: "fa-solid fa-magnifying-glass-chart",
        title: "SEO & optimalizace",
        desc: "Technická optimalizace pro vyhledávače, rychlost načítání a přístupnost. Váš web bude viditelný tam, kde ho zákazníci hledají.",
        tags: ["Lighthouse", "Core Web Vitals", "Analytics"],
    },
]

const steps = [
    {
        num: "01",
        title: "Konzultace",
        desc: "Společně probereme vaše potřeby, cíle a představy. Pochopím kontext vašeho podnikání.",
    },
    {
        num: "02",
        title: "Návrh",
        desc: "Připravím wireframy a vizuální návrh. Iterujeme dokud nebude vše přesně podle vašich představ.",
    },
    {
        num: "03",
        title: "Vývoj",
        desc: "Kóduji s důrazem na kvalitu, výkon a čistý kód. Průběžně ukazuji postup a sbírám zpětnou vazbu.",
    },
    {
        num: "04",
        title: "Spuštění",
        desc: "Důkladné testování, nasazení na produkci a předání. Zůstávám k dispozici pro další rozvoj.",
    },
]

const faq = [
    {
        q: "Kolik stojí web nebo aplikace na míru?",
        a: "Cena vždy vychází z rozsahu projektu. Po úvodní nezávazné konzultaci připravím konkrétní nabídku — buď fixní cenu za jasně zadaný projekt, nebo hodinovou sazbu u dlouhodobé spolupráce a průběžného rozvoje.",
    },
    {
        q: "Jak dlouho trvá vytvoření webu?",
        a: "Jednodušší prezentační web bývá hotový v řádu týdnů, rozsáhlejší webová aplikace nebo interní systém v řádu měsíců. Konkrétní harmonogram je vždy součástí nabídky.",
    },
    {
        q: "S jakými technologiemi pracujete?",
        a: "Primárně React, Next.js, TypeScript a Node.js; na backendu také PHP (Laravel, Nette) a databáze MySQL či PostgreSQL. Mobilní aplikace stavím v React Native nebo jako PWA. Technologii volím podle potřeb projektu, ne naopak.",
    },
    {
        q: "Pracujete i pro klienty mimo Pardubice a Hradec Králové?",
        a: "Ano. Osobní schůzky jsou možné v Pardubicích a Hradci Králové, většina spolupráce ale probíhá vzdáleně s klienty z celé České republiky i ze zahraničí.",
    },
    {
        q: "Postaráte se i o hosting, doménu a údržbu?",
        a: "Ano — součástí nabídky je správa hostingu, domény, DNS, firemních e-mailů a SSL certifikátů i průběžná údržba a rozvoj webu po spuštění.",
    },
    {
        q: "Převezmete i existující projekt?",
        a: "Ano, běžně navazuji na existující weby a aplikace — ať už jde o opravy, redesign, optimalizaci výkonu a SEO, nebo napojení na další systémy.",
    },
]

const SERVICES_URL = "https://puhony.eu/sluzby/"

const slugify = (value: string) =>
    value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/&/g, "a")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")

const servicesJsonLd = [
    {
        "@type": "ItemList",
        "@id": `${SERVICES_URL}#services`,
        "name": "Služby webového vývoje",
        "itemListOrder": "https://schema.org/ItemListUnordered",
        "numberOfItems": services.length,
        "itemListElement": services.map((service, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "item": {
                "@type": "Service",
                "@id": `${SERVICES_URL}#${slugify(service.title)}`,
                "url": `${SERVICES_URL}#${slugify(service.title)}`,
                "name": service.title,
                "description": service.desc,
                "keywords": service.tags.join(", "),
                "serviceType": service.title,
                "provider": {"@id": "https://puhony.eu/#person"},
                "areaServed": {"@type": "Country", "name": "Česká republika"},
                "availableChannel": {
                    "@type": "ServiceChannel",
                    "serviceUrl": "https://puhony.eu/kontakt/",
                    "availableLanguage": ["cs", "en"],
                },
            },
        })),
    },
    {
        "@type": "HowTo",
        "@id": `${SERVICES_URL}#postup`,
        "name": "Jak probíhá spolupráce — od nápadu k realitě",
        "description": "Čtyři kroky spolupráce na webu nebo aplikaci: konzultace, návrh, vývoj a spuštění.",
        "step": steps.map((step, i) => ({
            "@type": "HowToStep",
            "position": i + 1,
            "name": step.title,
            "text": step.desc,
        })),
    },
    {
        "@type": "FAQPage",
        "@id": `${SERVICES_URL}#faq`,
        "mainEntity": faq.map(item => ({
            "@type": "Question",
            "name": item.q,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": item.a,
            },
        })),
    },
]

const Services = () => {
    return (
        <Layout>
            <SEO
                title="Služby — Webové stránky, aplikace a systémy"
                breadcrumb="Služby"
                description="Webové stránky, PWA a mobilní aplikace, webové aplikace na míru, propojení systémů, správa a IT služby, SEO optimalizace. Kompletní digitální řešení od web developera z Pardubic."
                jsonLd={servicesJsonLd}
            />
            <Center>
                <PageTitle>Služby</PageTitle>
                <Subtitle>
                    Pomáhám firmám a jednotlivcům budovat jejich digitální přítomnost.
                    Od prvního nápadu po&nbsp;funkční produkt.
                </Subtitle>
            </Center>

            <BigCenter>
                <ServiceGrid>
                    {services.map((service, i) => (
                        <ServiceCard key={i} id={slugify(service.title)} style={{animationDelay: `${i * 0.07}s`}}>
                            <CardNumber>{String(i + 1).padStart(2, '0')}</CardNumber>
                            <CardIcon className={service.icon} />
                            <CardTitle>{service.title}</CardTitle>
                            <CardDesc>{service.desc}</CardDesc>
                            <TagRow>
                                {service.tags.map((tag, j) => (
                                    <Tag key={j}>{tag}</Tag>
                                ))}
                            </TagRow>
                        </ServiceCard>
                    ))}
                </ServiceGrid>
            </BigCenter>

            <DarkSection>
                <Center>
                    <ProcessLabel>Jak pracuji</ProcessLabel>
                    <ProcessTitle>Od nápadu k&nbsp;realitě</ProcessTitle>
                    <StepsGrid>
                        {steps.map((step, i) => (
                            <Step key={i}>
                                <StepNum>{step.num}</StepNum>
                                <StepContent>
                                    <StepTitle>{step.title}</StepTitle>
                                    <StepDesc>{step.desc}</StepDesc>
                                </StepContent>
                                {i < steps.length - 1 && <StepConnector />}
                            </Step>
                        ))}
                    </StepsGrid>
                    <ProcessCTA>
                        <Link to="/kontakt/"><CtaBtn>Začněme spolupráci</CtaBtn></Link>
                    </ProcessCTA>
                </Center>
            </DarkSection>

            <FaqSection id="faq" aria-labelledby="faq-title">
                <Center>
                    <FaqLabel>FAQ</FaqLabel>
                    <FaqTitle id="faq-title">Časté otázky</FaqTitle>
                    <FaqList>
                        {faq.map((item, i) => (
                            <FaqItem key={i} open={i === 0}>
                                <FaqQuestion>
                                    <span>{item.q}</span>
                                    <i className="fa-solid fa-chevron-down" aria-hidden="true" />
                                </FaqQuestion>
                                <FaqAnswer>{item.a}</FaqAnswer>
                            </FaqItem>
                        ))}
                    </FaqList>
                </Center>
            </FaqSection>

            <CTA />
        </Layout>
    )
}

export default Services

/* ---- animations ---- */

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

/* ---- hero area ---- */

const Subtitle = styled.p`
  text-align: center;
  max-width: 34em;
  margin: -1.5em auto 5em;
  font-size: 1.15em;
  line-height: 1.7;
  opacity: 0.55;
  font-weight: 300;
`

/* ---- service grid ---- */

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5em;
  margin-bottom: 8em;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 1.2em;
  }
`

const ServiceCard = styled.div`
  position: relative;
  background: #f7f7f7;
  border: 1px solid #ebebeb;
  border-radius: 0.6em;
  padding: 2.4em 2em 2em;
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  animation: ${fadeUp} 0.5s ease both;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.06);
    border-color: #d0d0d0;
  }
`

const CardNumber = styled.span`
  position: absolute;
  top: -0.18em;
  right: 0.12em;
  font-size: 5em;
  font-weight: 900;
  line-height: 1;
  color: rgba(0, 0, 0, 0.03);
  pointer-events: none;
  user-select: none;
`

const CardIcon = styled.i`
  font-size: 1.5em;
  color: rgba(25, 25, 25, 0.7);
  margin-bottom: 0.8em;
  display: block;
`

const CardTitle = styled.h3`
  font-size: 1.15em;
  font-weight: 700;
  margin: 0 0 0.6em;
  letter-spacing: -0.01em;
`

const CardDesc = styled.p`
  font-size: 0.88em;
  line-height: 1.7;
  margin: 0 0 1.4em;
  opacity: 0.6;
  font-weight: 300;
`

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4em;
`

const Tag = styled.span`
  font-size: 0.72em;
  font-weight: 400;
  background: rgba(25, 25, 25, 0.06);
  color: rgba(25, 25, 25, 0.55);
  padding: 0.3em 0.7em;
  border-radius: 2em;
  letter-spacing: 0.02em;
`

/* ---- dark process section ---- */

const DarkSection = styled.section`
  background: rgba(25, 25, 25, 1);
  color: #fff;
  padding: 7em 0 6em;
  margin-top: 2em;
`

const ProcessLabel = styled.span`
  display: block;
  text-align: center;
  font-size: 0.8em;
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  opacity: 0.4;
  margin-bottom: 1em;
`

const ProcessTitle = styled.h2`
  text-align: center;
  font-size: 2.8em;
  font-weight: 800;
  margin: 0 0 2.2em;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 2em;
  }
`

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  position: relative;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 2em;
  }
`

const Step = styled.div`
  position: relative;
  padding: 0 1.5em;
  text-align: center;

  @media (max-width: 800px) {
    display: grid;
    grid-template-columns: 3.5em 1fr;
    text-align: left;
    padding: 0;
    gap: 1em;
    align-items: start;
  }
`

const StepNum = styled.span`
  display: block;
  font-size: 2.4em;
  font-weight: 900;
  opacity: 0.12;
  margin-bottom: 0.4em;
  letter-spacing: -0.03em;

  @media (max-width: 800px) {
    font-size: 1.8em;
    opacity: 0.15;
    margin: 0;
    text-align: center;
    padding-top: 0.1em;
  }
`

const StepContent = styled.div``

const StepTitle = styled.h4`
  font-size: 1.05em;
  font-weight: 700;
  margin: 0 0 0.5em;
`

const StepDesc = styled.p`
  font-size: 0.85em;
  line-height: 1.7;
  margin: 0;
  opacity: 0.45;
  font-weight: 300;
`

const StepConnector = styled.div`
  position: absolute;
  top: 1.2em;
  right: -0.5em;
  width: 1em;
  height: 1px;
  background: rgba(255, 255, 255, 0.12);

  @media (max-width: 800px) {
    display: none;
  }
`

/* ---- FAQ ---- */

const FaqSection = styled.section`
  padding: 6em 0 1em;
`

const FaqLabel = styled.span`
  display: block;
  text-align: center;
  font-size: 0.8em;
  font-weight: 400;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  opacity: 0.45;
  margin-bottom: 1em;
`

const FaqTitle = styled.h2`
  text-align: center;
  font-size: 2.8em;
  font-weight: 800;
  margin: 0 0 1.4em;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 2em;
  }
`

const FaqList = styled.div`
  max-width: 44em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0.6em;
`

const FaqItem = styled.details`
  border: 1px solid #ebebeb;
  border-radius: 0.6em;
  padding: 0 1.4em;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &[open] {
    border-color: #d8d8d8;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
  }

  &[open] summary i {
    transform: rotate(180deg);
  }
`

const FaqQuestion = styled.summary`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1em;
  cursor: pointer;
  list-style: none;
  padding: 1.1em 0;
  font-weight: 600;
  font-size: 1.02em;
  line-height: 1.4;

  &::-webkit-details-marker {
    display: none;
  }

  i {
    font-size: 0.75em;
    opacity: 0.4;
    transition: transform 0.2s ease;
    flex-shrink: 0;
  }
`

const FaqAnswer = styled.p`
  margin: 0;
  padding: 0 0 1.3em;
  font-size: 0.95em;
  line-height: 1.75;
  opacity: 0.6;
  font-weight: 300;
`

const ProcessCTA = styled.div`
  text-align: center;
  margin-top: 4em;

  a {
    text-decoration: none;
  }
`

const CtaBtn = styled(Btn)`
  background: #fff;
  color: rgba(25, 25, 25, 1);
  font-weight: 700;
  padding: 0.85em 2em;
  font-size: 0.95em;
  letter-spacing: 0.01em;

  &:hover {
    background: #e8e8e8;
  }
`
